"use client";

import { useId, type CSSProperties } from "react";
import clsx from "clsx";
import { stegaClean } from "next-sanity";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { Button } from "@/components/shared/Button/Button";
import { Picture } from "@/components/shared/Picture/Picture";
import { PortableTextRichText } from "@/components/shared/PortableTextRichText/PortableTextRichText";
import ArrowIcon from "@/images/icons/arrow-button.svg";
import { PageQueryBySlugTasResult } from "@/sanity/types";
import { BREAKPOINTS } from "@/utilities/breakpoints";
import { kebabCase } from "@/utilities/string-helpers";
import styles from "./ImageGalleryBlock.module.scss";

type ImageGalleryBlockProps = Pick<
	Extract<
		NonNullable<NonNullable<PageQueryBySlugTasResult>["content"]>[number],
		{
			_type: "image-gallery-block";
		}
	>,
	"title" | "body" | "images" | "theme"
> & {
	customId?: string | null;
	className?: string;
};

export const ImageGalleryBlock = ({
	title,
	body,
	images,
	theme,
	customId,
	className,
}: ImageGalleryBlockProps) => {
	// Scope Swiper's controls to this instance, including galleries inside tabs.
	const id = `image-gallery-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
	const cleanTheme = stegaClean(theme);
	const slides = images?.filter(item => item.image?.asset) ?? [];

	if (!slides.length) return null;

	return (
		<section
			id={customId ? kebabCase(customId) : undefined}
			className={clsx(
				styles["image-gallery"],
				cleanTheme && styles[`image-gallery--${cleanTheme}`],
				className,
			)}
			data-theme={cleanTheme}
		>
			<div className={styles["image-gallery__inner"]}>
				{title?.length ? (
					<h2 className={styles["image-gallery__heading"]}>
						<PortableTextRichText
							asFragment
							value={title}
						/>
					</h2>
				) : null}
				<Swiper
					className={styles["image-gallery__swiper"]}
					modules={[Navigation, Pagination, A11y]}
					slidesPerView="auto"
					spaceBetween={20}
					breakpoints={{ [BREAKPOINTS.lg]: { spaceBetween: 40 } }}
					speed={666}
					watchOverflow
					observer
					observeParents
					navigation={{ nextEl: `#${id}-next`, prevEl: `#${id}-prev` }}
					pagination={{ el: `#${id}-pagination`, clickable: true }}
					a11y={{
						containerRoleDescriptionMessage: "Image gallery",
						prevSlideMessage: "Previous images",
						nextSlideMessage: "Next images",
					}}
				>
					{slides.map(item => {
						const aspectRatio = item.aspectRatio || 1;
						return (
							<SwiperSlide
								key={item._key}
								className={styles["image-gallery__slide"]}
								style={{ "--image-aspect-ratio": aspectRatio } as CSSProperties}
							>
								<Picture
									className={styles["image-gallery__image"]}
									sanityImage={item.image}
									alt={item.altText}
									width={Math.round(280 * aspectRatio)}
									height={280}
									crop={false}
									eager
									sizes={{
										[BREAKPOINTS.md]: { width: Math.round(400 * aspectRatio), height: 400 },
										[BREAKPOINTS.lg]: { width: Math.round(480 * aspectRatio), height: 480 },
									}}
								/>
							</SwiperSlide>
						);
					})}
					<div
						className={styles["controls"]}
						slot="container-end"
					>
						<div
							id={`${id}-pagination`}
							className={styles["controls__pagination"]}
						/>
						<div className={styles["controls__navigation"]}>
							<Button
								id={`${id}-prev`}
								type="button"
								className={styles["controls__previous"]}
								label={<ArrowIcon aria-hidden="true" />}
								aria-label="Previous images"
							/>
							<Button
								id={`${id}-next`}
								type="button"
								label={<ArrowIcon aria-hidden="true" />}
								aria-label="Next images"
							/>
						</div>
					</div>
				</Swiper>
				{body?.length ? (
					<div className={styles["image-gallery__body"]}>
						<PortableTextRichText value={body} />
					</div>
				) : null}
			</div>
		</section>
	);
};
